// หนึ่งบรรทัดในรายการไฟล์ของเมนู Finance
//
// หมวดกับแคมเปญเป็น dropdown ในแถวเลย ไม่ต้องเข้าโหมดแก้ไข — สองค่านี้คือสิ่งที่
// ผู้ใช้ปรับบ่อยที่สุดหลังอัปโหลด (โจทย์บอกว่า "กำหนดตอนอัปโหลด หรือปรับทีหลังก็ได้")
import type { Campaign } from "@contracts/api";
import {
  documentFileUrl,
  type CampaignDocument,
  type FinanceCategory,
} from "@features/documents/api/documents-api";
import { fileKind } from "@features/documents/lib/file-kind";
import { fmtInt } from "@shared/lib/format";
import { IconDownload, IconTrash } from "@shared/ui/icons";
import { FINANCE_CATEGORIES } from "../lib/categories";

const KIND_ICON: Record<string, string> = {
  image: "🖼️",
  pdf: "📕",
  sheet: "📊",
  slides: "📈",
  doc: "📄",
  text: "📄",
  other: "📎",
};

/** ขนาดไฟล์แบบอ่านง่าย — ต่ำกว่า 1MB บอกเป็น KB ไม่งั้นจะเห็น "0.02 MB" เต็มไปหมด */
function sizeText(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${fmtInt(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

export function FinanceRow({
  doc,
  campaigns,
  busy,
  onOpen,
  onCategory,
  onCampaign,
  onDelete,
}: {
  doc: CampaignDocument;
  campaigns: Campaign[];
  busy: boolean;
  onOpen: () => void;
  onCategory: (category: FinanceCategory) => void;
  onCampaign: (campaignId: string) => void;
  onDelete: () => void;
}) {
  const kind = fileKind({ name: doc.name, mime: doc.mime });

  return (
    <div className="fin-row">
      <span className="fin-ic" aria-hidden>
        {KIND_ICON[kind] ?? KIND_ICON.other}
      </span>

      <button type="button" className="fin-name" onClick={onOpen} title="เปิดดูไฟล์">
        <span className="fin-name-text">{doc.name}</span>
        <span className="muted small-note">
          {sizeText(doc.size)} · {new Date(doc.createdAt).toLocaleDateString("th-TH")}
        </span>
      </button>

      <select
        className="fin-pick"
        value={doc.category ?? "other"}
        disabled={busy}
        onChange={(e) => onCategory(e.target.value as FinanceCategory)}
      >
        {FINANCE_CATEGORIES.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </select>

      <select
        className="fin-pick"
        value={doc.campaignId ?? ""}
        disabled={busy}
        onChange={(e) => onCampaign(e.target.value)}
      >
        <option value="">— ไม่ระบุแคมเปญ —</option>
        {campaigns.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>

      <a
        className="icon-btn"
        href={documentFileUrl(doc.id, { download: true })}
        title="ดาวน์โหลด"
        download={doc.name}
      >
        <IconDownload size={18} />
      </a>
      <button
        type="button"
        className="icon-btn"
        title="ลบไฟล์"
        disabled={busy}
        onClick={() => {
          if (confirm(`ลบ "${doc.name}" ?`)) onDelete();
        }}
      >
        <IconTrash size={18} />
      </button>
    </div>
  );
}
